import { SharedDealService } from "@js/services/Deal/SharedDealService";
import {
  AccountClient,
  ApplicationUser,
  ApplicationUserCrudClient,
  AppraisalModuleEnum,
  BridgingDeal,
  CaseAccessLevelEnum,
  CommercialDeal,
  DealClient,
  DealLender,
  DealLenderClient,
  DealNote,
  DealNoteClient,
  DealNoteCrudClient,
  DealNoteInsert,
  DevelopmentFinanceDeal,
  LicenseMasterStatusEnum,
} from "@src/apis/ModularClient";
import { IndexService } from "@js/services/IndexService";
import { SelectListService } from "@js/services/SelectListService";
import { getOneUsingOData } from "@ccq/ts-lib";
import { RoleService } from "@js/services/RoleService";

export class BaseCaseController<T extends BridgingDeal | CommercialDeal | DevelopmentFinanceDeal> {
  dealDto: T;
  dealId: number;
  dealForm: ng.IFormController;
  messageContent: string;
  showRequestEmailForm: boolean = false;
  sendingMessage: boolean = false;
  appName: string;
  caseStatusOptions = [];
  requestMessageReason: string = "";
  shareNote: string;
  shareContext: AppraisalModuleEnum = null;
  phoneNumber: string;
  dataLoading: boolean;
  newNote: DealNoteInsert = {
    deal: null,
    noteText: "",
    ownersApplicationUserGroups: [],
    ownersApplicationUsers: [],
    user: null,
    dealId: 0,
    userId: 0,
  };
  isAdmin: boolean = false;
  isLender: boolean = false;
  isBroker: boolean = false;
  isClient: boolean = false;
  dealLender: DealLender;
  caseStatusText: string = "";
  isConnectUser: boolean;
  isInActiveLender: boolean = false;
  currentUserFullName: string = "";
  //property for roles directive to handle referred readonly view for broker/connect broker
  isReadOnlyDeal: boolean = true;

  //overview properties
  caseOverview: string = "";
  tabChangePending: number = 0;
  showUnsavedChangesModal: boolean = false;
  pendingTabCallback = null;
  isCaseOverviewFocused: boolean = false;
  unsavedKind: string = "";
  unsavedPromptText: string = "";
  isOverviewFocused: boolean = false;
  isNoteFocused: boolean = false;
  suppressNextBlurPrompt: boolean = false;
  isHandlingLocationChange: boolean = false;
  skipNextLocationChange: boolean = false;

  isRoleEvaluationComplete: boolean = false;
  isInvitedAndHasReadonlyAccess: boolean = true;
  requestAssistancePhoneNumber: string;
  static $inject = [
    "SharedDealService",
    "IndexService",
    "DealNoteCrudClient",
    "AccountClient",
    "DealNoteClient",
    "DealClient",
    "SelectListService",
    "$location",
    "$rootScope",
    "RoleService",
    "ApplicationUserCrudClient",
    "DealLenderClient",
    "$timeout",
  ];

  constructor(
    protected sharedDealService: SharedDealService,
    protected indexService: IndexService,
    protected dealNoteCrudClient: DealNoteCrudClient,
    protected accountClient: AccountClient,
    protected dealNoteClient: DealNoteClient,
    protected dealClient: DealClient,
    protected selectListService: SelectListService,
    protected $location: ng.ILocationService,
    protected $rootScope: ng.IRootScopeService,
    protected roleService: RoleService,
    protected applicationUserCrudClient: ApplicationUserCrudClient,
    protected dealLenderClient: DealLenderClient,
    protected $timeout: ng.ITimeoutService,
  ) {
    $rootScope.$on("$locationChangeStart", (event, newUrl) => {
      const newPath = newUrl.split("#!")[1] || "/";

      if (this.isHandlingLocationChange) return;

      const overviewDirty = this.caseOverview !== this.dealDto.projectDescription;
      const noteText = this.newNote?.noteText?.trim?.() ?? "";
      const noteDirty = noteText.length > 0;

      if (overviewDirty || noteDirty) {
        event.preventDefault();

        this.pendingTabCallback = () => {
          this.isHandlingLocationChange = true;

          this.$location.path(newPath);

          this.$timeout(() => {
            this.isHandlingLocationChange = false;
          }, 100);
        };

        overviewDirty ? this.setUnsavedChangePrompt("caseOverview") : this.setUnsavedChangePrompt("dealNote");

        return;
      }

      this.isHandlingLocationChange = true;
      this.$timeout(() => {
        this.$location.path(newPath);
      }, 0);
    });
  }

  constructShareNoteMessage() {
    this.shareNote = this.sharedDealService.constructShareNoteMessage(this.shareContext, this.phoneNumber);
  }

  async saveNote(): Promise<void> {
    this.newNote.dealId = this.dealDto.id;
    this.newNote.userId = await this.indexService.currentUserId;
    this.newNote.deal = null;
    this.dataLoading = true;
    this.dealNoteCrudClient
      .post(this.newNote)
      .then((response) => {
        // Hack to display the author's name immediately after adding a deal note.
        response.user = response.user ?? ({} as ApplicationUser);
        response.user.fullName = this.currentUserFullName;
        response.user.id = response.userId;
        this.dealDto.dealNotes.push(response);
        this.newNote = {
          deal: null,
          noteText: "",
          ownersApplicationUserGroups: [],
          ownersApplicationUsers: [],
          user: null,
          dealId: 0,
          userId: 0,
        };
        this.dealForm.$setPristine();
      })
      .finally(() => {
        this.dataLoading = false;
      });
  }

  async updateRequestAssitanceDealNoteMessage() {
    const userId = await this.indexService.currentUserId;
    if (this.dealDto?.dealNotes != null) {
      this.dealDto.dealNotes = this.dealDto.dealNotes.map((d) => {
        if (
          d.userId != userId &&
          (d.noteText == "Your have requested for assistance." || d.noteText == "You have requested assistance.")
        ) {
          return { ...d, NoteText: `${d.user.fullName} requested assistance.` };
        }

        return d;
      });
    }
  }

  async sendRequestEmail() {
    this.showRequestEmailForm = false;
    this.sendingMessage = true;
    this.dataLoading = true;
    try {
      await this.accountClient.requestAssistanceReview(
        this.requestMessageReason,
        this.dealDto.id,
        this.dealDto.productFamily,
        this.requestAssistancePhoneNumber,
      );

      this.showMessage(
        `${this.appName} have been notified that you require assistance and will be in touch shortly to help.`,
      );

      const dealNote: DealNote = {
        dealId: this.dealDto.id,
        userId: await this.indexService.currentUserId,
        noteText: "You have requested " + this.requestMessageReason + ".",

        createdDateTime: undefined,
        createdUser: undefined,
        createdUserId: null,
        deal: undefined,
        id: null,
        isDeleted: false,
        lastModifiedUser: undefined,
        lastModifiedUserId: null,
        lastUpdatedDateTime: undefined,
        ownersApplicationUserGroups: [],
        ownersApplicationUsers: [],
        user: undefined,
        /*ExpandNote: false*/
      };
      this.dealNoteClient.addUpdate(dealNote).then((caseNoteResponse) => {
        this.dealDto.dealNotes.push(caseNoteResponse);
      });
    } catch (error) {
      this.showMessage("Sorry, something went wrong while sending your email. Please try again.");
    } finally {
      this.sendingMessage = false;
      this.dataLoading = false;
      this.requestAssistancePhoneNumber = this.phoneNumber;
    }
  }

  showMessage(message: string) {
    this.messageContent = message;
  }

  async checkIfUserIsInvited() {
    //added to scope for directive
    const response = await this.dealClient.isUserInvitedToDeal({
      userId: await this.indexService.currentUserId,
      dealId: this.dealId,
    });

    const licenceData = await this.indexService.currentUserLicenseData;
    const subscriptionStatus = licenceData.subscriptionStatus;

    // if user has no active subscription (and are not a lender or admin) then they should be redirected back to the dashboard
    if (
      subscriptionStatus != LicenseMasterStatusEnum.PaidUp &&
      subscriptionStatus != LicenseMasterStatusEnum.PreCancel &&
      subscriptionStatus != LicenseMasterStatusEnum.PaymentProcessing &&
      !response.isInvited &&
      !this.isAdmin &&
      !this.isLender
    ) {
      this.$location.path("/dashboard");
      return;
    }

    (this.$rootScope as any).isInvitedToCase = response.isInvited;

    if (response.isInvited) {
      if (response.dealAccessLevel == CaseAccessLevelEnum.ReadOnly) {
        (this.$rootScope as any).isInvitedAndHasReadonlyAccess = true;
      } else {
        this.isInvitedAndHasReadonlyAccess = false; // show lendershortlist & resuts button to clients.
      }
    } else {
      this.isInvitedAndHasReadonlyAccess = false; // show lendershortlist & resuts button to non-client users.
    }

    this.$rootScope.$broadcast("invitedStatusChange");
  }

  generateCaseStatusText() {
    if (this.isLender) {
      this.caseStatusOptions = this.selectListService.GetCaseLenderStateOptionsForLenders();
    } else {
      this.caseStatusOptions = this.selectListService.GetCaseStatusOptions(this.isConnectUser);
    }
    this.getCaseStatusDisplayText();
  }

  /**Gets the Case Status display text */
  getCaseStatusDisplayText() {
    let statusItem = null;

    if (this.isLender) {
      statusItem = this.caseStatusOptions.find((statusOption) => this.dealLender.status == statusOption.key);
    } else {
      statusItem = this.caseStatusOptions.find((statusOption) => this.dealDto.status == statusOption.key);
    }

    this.caseStatusText = statusItem.displayName;
  }

  showEditDealNameButton() {
    return !this.isReadOnlyDeal && (!this.isConnectUser || (this.isConnectUser && !this.dealDto.isReferredToConnect));
  }

  async getCurrentUserAndRoles() {
    [this.isClient, this.isAdmin, this.isBroker, this.isLender] = await Promise.all([
      this.roleService.isClientUser(),
      this.roleService.isAdminUser(),
      this.roleService.isBroker(),
      this.roleService.isLenderUser(),
    ]);

    this.isRoleEvaluationComplete = true;
    if (this.isLender) {
      this.dealLenderClient.fetchDealLender(this.dealDto.id).then((response) => {
        this.dealLender = response;
        this.isInActiveLender = response.isLenderInActive;
        this.generateCaseStatusText();
      });
    }

    const { phoneNumber, isConnectUser, firstName, lastName } = await getOneUsingOData(this.applicationUserCrudClient, {
      filter: { id: { eq: await this.indexService.currentUserId } },
      select: { phoneNumber: true, isConnectUser: true, firstName: true, lastName: true },
    });
    this.phoneNumber = this.requestAssistancePhoneNumber = phoneNumber;
    this.isConnectUser = isConnectUser;
    this.currentUserFullName = `${firstName} ${lastName}`;
    this.checkIfUserIsInvited();
    this.updateRequestAssitanceDealNoteMessage();
    if (!this.isLender) this.generateCaseStatusText();
  }

  goToTab(label, callback) {
    const overviewDirty = this.caseOverview !== this.dealDto.projectDescription;
    const noteText = this.newNote && this.newNote.noteText ? String(this.newNote.noteText).trim() : "";
    const noteDirty = noteText.length > 0;

    let kind = null;
    if (overviewDirty) {
      kind = "caseOverview";
    } else if (noteDirty) {
      kind = "dealNote";
    }

    if (kind) {
      this.tabChangePending = label;
      this.pendingTabCallback = callback;
      this.setUnsavedChangePrompt(kind);
    } else {
      callback.call(this);
    }
  }

  confirmTabChangeWithoutSave() {
    if (this.unsavedKind === "caseOverview") {
      this.caseOverview = this.dealDto.projectDescription;
    } else if (this.unsavedKind === "dealNote") {
      this.newNote.noteText = "";
    }
    if (this.pendingTabCallback) {
      this.pendingTabCallback.call(this);
    }
    this.resetUnsavedChangePrompt();
  }

  cancelTabChange() {
    this.resetUnsavedChangePrompt();
  }

  resetUnsavedChangePrompt() {
    this.showUnsavedChangesModal = false;
    this.tabChangePending = null;
    this.pendingTabCallback = null;
    this.unsavedKind = null;
    this.unsavedPromptText = null;
  }

  onOverviewFocus() {
    this.isOverviewFocused = true;
  }

  onDealNoteFocus() {
    this.isNoteFocused = true;
  }

  handleOverviewBlur() {
    if (this.suppressNextBlurPrompt) {
      this.suppressNextBlurPrompt = false;
      return;
    }
    this.$timeout(() => {
      const isDirty = this.caseOverview !== this.dealDto.projectDescription;
      if (isDirty && !this.showUnsavedChangesModal) {
        this.tabChangePending = null;
        this.pendingTabCallback = null;
        this.setUnsavedChangePrompt("caseOverview");
      }
      this.isOverviewFocused = false;
    }, 120);
  }

  handleDealNoteBlur() {
    if (this.suppressNextBlurPrompt) {
      this.suppressNextBlurPrompt = false;
      return;
    }
    this.$timeout(() => {
      const text = this.newNote && this.newNote.noteText ? String(this.newNote.noteText).trim() : "";
      const hasText = text.length > 0;
      if (hasText && !this.showUnsavedChangesModal) {
        this.tabChangePending = null;
        this.pendingTabCallback = null;
        this.setUnsavedChangePrompt("dealNote");
      }
      this.isNoteFocused = false;
    }, 120);
  }

  setUnsavedChangePrompt(kind: "caseOverview" | "dealNote") {
    this.unsavedKind = kind;
    this.unsavedPromptText =
      kind === "caseOverview"
        ? "Do you want to save your Case Overview changes?"
        : "Do you want to save your Private Case Notes changes?";

    this.showUnsavedChangesModal = true;
  }
}
